package com.eladfinish.bible.contest.screens.names

import androidx.compose.ui.text.TextRange
import com.eladfinish.bible.contest.SharedRes
import com.eladfinish.bible.contest.common.usecases.RemoveNikudUseCase
import com.eladfinish.bible.contest.constants.StringConstants.Regex.ALEF_TAV_
import com.eladfinish.bible.contest.constants.StringConstants.Regex.OTIOT_1_TO_3
import com.eladfinish.bible.contest.constants.StringConstants.Regex.WORD_CHAR
import com.eladfinish.bible.contest.data.common.stringResolver.StringResolver
import com.eladfinish.bible.contest.data.common.stringResolver.resolveStringSuspending
import com.eladfinish.bible.contest.extensions.end
import com.eladfinish.bible.contest.extensions.replaceRegex
import com.eladfinish.bible.contest.extensions.splitByRegex
import com.eladfinish.bible.contest.extensions.start
import com.eladfinish.bible.contest.names_topic_sentence
import com.eladfinish.bible.contest.network.ApiResult
import com.eladfinish.bible.contest.network.spreadsheet.GoogleSheetContent
import com.eladfinish.bible.contest.network.spreadsheet.GoogleSheetItem
import com.eladfinish.bible.contest.open_questions_credit_sentence
import com.eladfinish.bible.contest.open_questions_credit_sentence_linked_word
import com.eladfinish.bible.contest.screens.names.api.NamesGithubApi
import com.eladfinish.bible.contest.screens.names.data.Gender
import com.eladfinish.bible.contest.screens.names.data.NameItem
import com.eladfinish.bible.contest.screens.names.data.NameReference
import com.eladfinish.bible.contest.screens.names.data.NamesDatabase
import com.eladfinish.bible.contest.screens.names.data.NamesResult
import com.eladfinish.bible.contest.screens.names.data.toBibleName
import dev.icerock.moko.resources.desc.desc
import dev.icerock.moko.resources.format
import io.github.aakira.napier.Napier
import io.ktor.client.statement.HttpResponse
import io.ktor.http.HttpStatusCode
import kotlinx.coroutines.yield

sealed class ErrorCause {
    data object NoInternet : ErrorCause()
    data class Http(val statusCode: HttpStatusCode) : ErrorCause()
}

private const val NAMES_YIELD_EVERY = 20

fun interface NamesLoader {
    suspend operator fun invoke(gender: Gender, letter: String): NamesResult
}

class GetNamesUseCase(
    private val stringResolver: StringResolver,
    private val removeNikudUseCase: RemoveNikudUseCase,
    private val namesGithubApi: NamesGithubApi,
) : NamesLoader {

    override suspend operator fun invoke(gender: Gender, letter: String): NamesResult {
        val results: ApiResult<List<GoogleSheetContent>, Any> = namesGithubApi.getNamesResults(
            gender = gender.directoryName,
            letter = letter,
        )

        when (results) {
            // TODO: use .isInternetError()
            is ApiResult.ClientError -> {
                Napier.e(results.throwable.toString())
                val notFound = (results.errorBody as? HttpResponse)?.status == HttpStatusCode.NotFound
                if (notFound) {
                    return getNamesWithData(
                        apiResult = ApiResult.Success(emptyList()),
                        gender = gender,
                        letter = letter,
                    ).copy(
                        errorCause = ErrorCause.Http(HttpStatusCode.NotFound),
                    )
                }
            }

            // TODO: remove code duplication
            is ApiResult.Failure -> {
                Napier.e(results.cause.toString())
                return getNamesWithData(
                    apiResult = ApiResult.Success(emptyList()),
                    gender = gender,
                    letter = letter,
                ).copy(
                    errorCause = ErrorCause.NoInternet,
                )
            }

            is ApiResult.Success -> {}
        }

        // Built with a manual loop (not results.map { list.map {...} }) that yields periodically:
        // per-name work here is several regex passes each (getNameReferenceList), and a
        // letter can return 200+ names. That's synchronous CPU-bound work with no suspension
        // point in between — harmless on Android/iOS (a real background thread), but on
        // single-threaded Kotlin/JS it runs as one uninterrupted block that starves the browser's
        // render/input loop for its full duration, freezing the tab.
        val apiResult: ApiResult<List<NameItem>, Any> = when (results) {
            is ApiResult.Success -> {
                val nameItems = mutableListOf<NameItem>()
                results.data.forEachIndexed { index, content ->
                    content.toBibleName()?.let { bibleName ->
                        val description = bibleName.description
                        nameItems += NameItem(
                            name = bibleName.name,
                            description = description,
                            nameReferenceList = getNameReferenceList(description),
                        )
                    }

                    if (index % NAMES_YIELD_EVERY == 0) yield()
                }
                ApiResult.Success(nameItems.sortedBy { removeNikudUseCase(pasuk = it.name) })
            }

            is ApiResult.ClientError -> ApiResult.ClientError(results.errorCode, results.errorBody, results.throwable)
            is ApiResult.Failure -> ApiResult.Failure(results.cause)
        }
        return getNamesWithData(
            apiResult = apiResult,
            gender = gender,
            letter = letter,
        )
    }

    private fun getNameReferenceList(info: String): List<NameReference> {
        val nameReferenceList = mutableListOf<NameReference>()
        val input = info.replace(oldValue = "\n", newValue = " ")
            .replace(oldValue = "דברי הימים", newValue = "דה\"י")
        "\\(.*?\\)".toRegex().findAll(input).forEach {
            it.value.split(";").forEach { str ->
                nameReferenceList.addAll(getNameReferenceListInternal(info = "(${str.trim()})"))
            }
        }
        return nameReferenceList.distinct()
    }

    private fun getNameReferenceListInternal(info: String): MutableList<NameReference> {
        val nameReferenceList = mutableListOf<NameReference>()
        val bookPart = "[$ALEF_TAV_\"]+\\s?[א-ב]?"
        "\\(($bookPart) $OTIOT_1_TO_3,? $OTIOT_1_TO_3\\)".toRegex().findAll(info).forEach {
            with(it.groupValues) {
                nameReferenceList.add(
                    NameReference(
                        book = getOrNull(index = 1).orEmpty().expandBookAbbreviation(),
                        chapter = getOrNull(index = 2).orEmpty(),
                        verse = getOrNull(index = 3).orEmpty(),
                    )
                )
            }
        }
        return nameReferenceList
    }

    private suspend fun getNamesWithData(
        apiResult: ApiResult<List<NameItem>, Any>,
        gender: Gender,
        letter: String,
    ): NamesResult {
        val sbTopic = StringBuilder()
        val size = apiResult.dataOrNull()?.size ?: 0
        val genderString = stringResolver.resolveStringSuspending(gender.stringResource.desc())
        sbTopic
            .append(
                stringResolver.resolveStringSuspending(SharedRes.strings.names_topic_sentence.format(size, genderString, letter))
            )
            .append("\n")
            .append(
                stringResolver.resolveStringSuspending(SharedRes.strings.open_questions_credit_sentence.format(NamesDatabase.CREDIT_NAME))
            )
        val link = NamesDatabase.CREDIT_URL

        val linkedWord = stringResolver.resolveStringSuspending(SharedRes.strings.open_questions_credit_sentence_linked_word.desc())
        val linkRange = linkedWord.toRegex().find(sbTopic)!!.let { TextRange(start = it.start, end = it.end) }
        return NamesResult(
            apiResult = apiResult,
            titleSentence = sbTopic.toString(),
            linkedRange = linkRange,
            linkedString = linkedWord,
            link = link,
        )
    }

}

// (?!WORD_CHAR) rather than \b: JS RegExp's \b is ASCII-only (see WORD_CHAR)
internal fun String.expandBookAbbreviation(): String = this
    .replaceRegex(regex = "ישעיה(?!$WORD_CHAR)", replacement = "ישעיהו")
    .replaceRegex(regex = "ירמיה(?!$WORD_CHAR)", replacement = "ירמיהו")
    .replaceRegex(regex = "דה\"יא", replacement = "דברי הימים א")
    .replaceRegex(regex = "דה\"יב", replacement = "דברי הימים ב")
    .replaceRegex(regex = "דה\"י", replacement = "דברי הימים")
